import { useState } from "react";
import { Address, Hex } from "viem";
import { GRAPHQL_URL, ZERO_ADDRESS } from "~/core/constants";
import { formatBalance } from "~/core/utils/formatBalance";
import { useSound } from "./components/SoundHandler";
import { useAllBalance } from "~/core/hooks/useAllBalance";
import { useError } from "~/core/contexts/ErrorContext";
import { ContainerShape, InnerShape, HexagonShape } from "./components/Shape";
import Loader from "~/core/components/Loader";
import { TOKENS } from "~/core/tokens";
import { api, BlinkoGame } from "./api";
import { useBlinkoContract } from "./hooks/useBlinkoContract";
import { icons } from "./assets/icons/icons";
import { useNavigate } from "react-router-dom";
import { RESOLVER_ADDRESS } from "./constants";

type SubgraphActiveGame = {
  id: string;
};

type QueryResult = {
  games: SubgraphActiveGame[];
};

const fetchUserActiveGames = async (address: Address): Promise<QueryResult> => {
  try {
    const responseSubgraph = await fetch(GRAPHQL_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        query: `
          query {
            games (
              where: {
                player: "${address.toLowerCase()}",
                status: Active,
                resolver: "${RESOLVER_ADDRESS.toLowerCase()}",
              }
            ) {
              id
            }
          }
        `,
      }),
    });

    const { data } = await responseSubgraph.json();

    return {
      games: data.games || [],
    };
  } catch (e) {
    console.error(e);
    return { games: [] };
  }
};

export default function CsPage() {
  const [address, setAddress] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [winAmount, setWinAmount] = useState("0");
  const [claimableGames, setClaimableGames] = useState<BlinkoGame[]>([]);
  const [activeGames, setActiveGames] = useState<BlinkoGame[]>([]);
  const [searchCompleted, setSearchCompleted] = useState(false);
  const navigate = useNavigate();

  const onSearch = async () => {
    if (!address) return;

    setIsLoading(true);
    setSearchCompleted(false);
    setClaimableGames([]);
    setActiveGames([]);

    try {
      const data = await fetchUserActiveGames(address as Address);

      if (!data || data.games.length === 0) {
        setSearchCompleted(true);
        setIsLoading(false);
        return;
      }

      const activeGameIds = data.games.map((g) => g.id);
      const claimable: BlinkoGame[] = [];
      const active: BlinkoGame[] = [];

      console.log(data)

      await Promise.all(
        activeGameIds.map(async (id) => {
          try {
            const game = await api.getGame(id);
            if (game.status === "active") {
              active.push(game);
            } else if (game.status === "won") {
              claimable.push(game);
            }
          } catch (e) {
            console.error(`Failed to get game ${id}`, e);
          }
        })
      );

      const totalWin = claimable.reduce(
        (acc, game) => acc + BigInt(game.payoutAmount || "0"),
        0n
      );

      setWinAmount(formatBalance(totalWin, TOKENS[ZERO_ADDRESS].decimals));
      setClaimableGames(claimable);
      setActiveGames(active);
      setSearchCompleted(true);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const onGoHome = () => {
    navigate("/");
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#18181B] flex items-center justify-center">
        <div className="flex items-center space-x-2">
          <Loader stroke="white" size="24px" />
          <span className="text-white text-lg font-blinko">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#18181B] p-8">
      <div className="max-w-2xl mx-auto">
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <h1 className="text-4xl font-bold text-white font-blinko">
              Blinko CS
            </h1>
            <button onClick={onGoHome}>
              <HexagonShape cornerRadius={6} strokeWidth={1}>
                <div className="flex justify-center items-center p-2">
                  <img src={icons.arrowsLogout} className="w-6 h-6" />
                </div>
              </HexagonShape>
            </button>
          </div>
          <p className="text-white/70 mt-2">
            Check user game status and manage claims
          </p>
        </div>

        <ContainerShape cornerDiagonal={20} cornerRadius={8} strokeWidth={2}>
          <div className="p-6">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <label className="block text-sm font-medium text-white mb-2">
                  Wallet Address
                </label>
                <input
                  type="text"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  placeholder="Enter wallet address (0x...)"
                  className="w-full px-4 py-3 bg-black/30 border border-white/20 rounded-lg text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-[#F7931A] focus:border-transparent"
                />
              </div>
              <div className="flex items-end">
                <button
                  onClick={onSearch}
                  disabled={!address || isLoading}
                  className="disabled:opacity-50"
                >
                  <InnerShape cornerDiagonal={14} cornerRadius={8} strokeWidth={1}>
                    <div className="px-6 py-3 flex items-center gap-2">
                      <span className="text-white font-medium font-blinko">
                        Search
                      </span>
                      {isLoading && <Loader stroke="white" size="16px" />}
                    </div>
                  </InnerShape>
                </button>
              </div>
            </div>
          </div>
        </ContainerShape>

        {searchCompleted && (
          <div className="space-y-6 mt-6">
            {/* Summary Card */}
            {winAmount !== "0" && (
              <ContainerShape cornerDiagonal={16} cornerRadius={8} strokeWidth={1}>
                <div className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-white font-blinko">
                        Total Claimable
                      </h3>
                      <p className="text-2xl font-bold text-green-400">
                        {winAmount} ETH
                      </p>
                    </div>
                    <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center">
                      <img
                        src={TOKENS[ZERO_ADDRESS].icon}
                        className="w-6 h-6"
                        alt="ETH"
                      />
                    </div>
                  </div>
                </div>
              </ContainerShape>
            )}

            {/* Claimable Games */}
            {claimableGames.length > 0 && (
              <div className="space-y-4">
                <h2 className="text-2xl font-bold text-white font-blinko">
                  Claimable Rewards ({claimableGames.length})
                </h2>
                <div className="space-y-3">
                  {claimableGames.map((game) => (
                    <ClaimableGameCard key={game.gameId} game={game} />
                  ))}
                </div>
              </div>
            )}

            {/* Active Games */}
            {/* {activeGames.length > 0 && (
              <div className="space-y-4">
                <h2 className="text-2xl font-bold text-white font-blinko">
                  Active Games ({activeGames.length})
                </h2>
                <div className="space-y-3">
                  {activeGames.map((game) => (
                    <ActiveGameCard key={game.gameId} game={game} />
                  ))}
                </div>
              </div>
            )} */}

            {/* No Results */}
            {claimableGames.length === 0 && activeGames.length === 0 && (
              <ContainerShape cornerDiagonal={16} cornerRadius={8} strokeWidth={1}>
                <div className="p-8 text-center">
                  <div className="w-16 h-16 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <img src={icons.history} className="w-8 h-8 opacity-50" />
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-2 font-blinko">
                    No Active Games Found
                  </h3>
                  <p className="text-white/70">
                    This address has no claimable rewards or active games.
                  </p>
                </div>
              </ContainerShape>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ClaimableGameCard component
const ClaimableGameCard = ({ game }: { game: BlinkoGame }) => {
  const sounds = useSound();
  const { cashOutOnChain, settlingGames } = useBlinkoContract();
  const { mutate } = useAllBalance();
  const { handleError } = useError();
  const [isLoading, setIsLoading] = useState(false);

  const payoutAmount = BigInt(game.payoutAmount || "0");
  const isExpired = Number(game.deadline) * 1000 <= Date.now();
  const isSettling = settlingGames[game.gameId] || isLoading;

  const onClaim = async () => {
    if (isSettling || isExpired) return;

    sounds.play("click");
    setIsLoading(true);

    try {
      await cashOutOnChain({
        gameId: game.gameId as Hex,
        payoutAmount: payoutAmount,
        gameState: game.gameStateHash as Hex,
        gameSeed: game.gameSeed as Hex,
        deadline: BigInt(game.deadline),
        serverSignature: game.cashOutSignature as Hex,
      });
      mutate();
    } catch (error) {
      handleError("Failed to claim reward", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <ContainerShape cornerDiagonal={16} cornerRadius={8} strokeWidth={1}>
      <div className="p-6">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <div className="flex items-center space-x-3 mb-2">
              <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
              <h3 className="text-lg font-semibold text-white font-blinko">
                Game #{game.gameId.slice(0, 8)}...
              </h3>
              {isExpired && (
                <span className="px-2 py-1 bg-red-500/20 text-red-400 text-xs rounded-full">
                  Expired
                </span>
              )}
            </div>
            <div className="space-y-1">
              <p className="text-sm text-white/70">
                Bet:{" "}
                <span className="text-white">
                  {formatBalance(
                    BigInt(game.betAmount),
                    TOKENS[ZERO_ADDRESS].decimals
                  )}{" "}
                  ETH
                </span>
              </p>
              <p className="text-sm text-white/70">
                Reward:{" "}
                <span className="text-green-400 font-semibold">
                  {formatBalance(payoutAmount, TOKENS[ZERO_ADDRESS].decimals)}{" "}
                  ETH
                </span>
              </p>
              <p className="text-sm text-white/70">
                Deadline:{" "}
                <span className="text-white">
                  {new Date(Number(game.deadline) * 1000).toLocaleString()}
                </span>
              </p>
            </div>
          </div>
          <div className="ml-4">
            <button
              onClick={onClaim}
              disabled={isSettling || isExpired}
              className="disabled:opacity-50"
            >
              <InnerShape cornerDiagonal={12} cornerRadius={6} strokeWidth={1}>
                <div className="px-4 py-2 flex items-center gap-2">
                  {isSettling ? (
                    <Loader stroke="white" size="16px" />
                  ) : (
                    <span className="text-white font-medium font-blinko">
                      {isExpired ? "Expired" : "Claim"}
                    </span>
                  )}
                </div>
              </InnerShape>
            </button>
          </div>
        </div>
      </div>
    </ContainerShape>
  );
};

// ActiveGameCard component
const ActiveGameCard = ({ game }: { game: BlinkoGame }) => {
  const sounds = useSound();
  const navigate = useNavigate();

  const onViewReplay = () => {
    sounds.play("click");
    navigate(`/replay/${game.gameId}`);
  };

  return (
    <ContainerShape cornerDiagonal={16} cornerRadius={8} strokeWidth={1}>
      <div className="p-6">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <div className="flex items-center space-x-3 mb-2">
              <div className="w-3 h-3 bg-blue-500 rounded-full animate-pulse"></div>
              <h3 className="text-lg font-semibold text-white font-blinko">
                Game #{game.gameId.slice(0, 8)}...
              </h3>
              <span className="px-2 py-1 bg-blue-500/20 text-blue-400 text-xs rounded-full">
                In Progress
              </span>
            </div>
            <div className="space-y-1">
              <p className="text-sm text-white/70">
                Bet:{" "}
                <span className="text-white">
                  {formatBalance(
                    BigInt(game.betAmount),
                    TOKENS[ZERO_ADDRESS].decimals
                  )}{" "}
                  ETH
                </span>
              </p>
              <p className="text-sm text-white/70">
                Created:{" "}
                <span className="text-white">
                  {new Date(game.createdAt).toLocaleString()}
                </span>
              </p>
              <p className="text-sm text-white/70">
                Status: <span className="text-blue-400">Waiting for result</span>
              </p>
            </div>
          </div>
          <div className="ml-4">
            <button onClick={onViewReplay}>
              <InnerShape cornerDiagonal={12} cornerRadius={6} strokeWidth={1}>
                <div className="px-4 py-2 flex items-center gap-2">
                  <span className="text-white font-medium font-blinko">View</span>
                </div>
              </InnerShape>
            </button>
          </div>
        </div>
      </div>
    </ContainerShape>
  );
};
